import { Fragment, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft,
  Building2,
  Calculator,
  Check,
  ChevronDown,
  ChevronRight,
  Copy,
  FolderKanban,
  History,
  Loader2,
  Package,
  Pencil,
  Plus,
  Trash2,
  X,
} from "lucide-react";
import { AppLayout } from "@/components/layout/AppLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select } from "@/components/ui/select";
import { ReferenceIdBadge } from "@/components/ui/ReferenceIdBadge";
import { ConfirmDialog } from "@/components/shared/ConfirmDialog";
import { ActivityHistoryDrawer } from "@/components/audit/ActivityHistoryDrawer";
import {
  useAddEstimationItem,
  useDeleteEstimation,
  useDeleteEstimationItem,
  useDuplicateEstimation,
  useEstimation,
  useUpdateEstimation,
  useUpdateEstimationItem,
  useUpdateEstimationStatus,
} from "../hooks/useEstimations";
import { useCategoryItems } from "@/modules/dropdowns/hooks/useDropdowns";
import { useCompanyList } from "@/modules/companies/hooks/useCompanies";
import { useProductList } from "@/modules/products/hooks/useProducts";
import { useProjectList } from "@/modules/projects/hooks/useProjects";
import { formatCurrency } from "@/modules/expenses/components/CurrencyAmount";
import { EstimationStatusBadge } from "./EstimationListPage";
import type { EstimationItem } from "../types";

const BORDER = "hsl(210,30%,85%)";

function formatDateTime(iso: string | null): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

type EditingRow = {
  id: string | null; // null = new row
  category: string;
  description: string;
  rateType: string;
  unitCost: string;
  quantity: string;
  notes: string;
};

function emptyRow(category = "human_resources", rateType = "monthly"): EditingRow {
  return {
    id: null,
    category,
    description: "",
    rateType,
    unitCost: "",
    quantity: "1",
    notes: "",
  };
}

function RowErrorBanner({ error }: { error: string | null }) {
  if (!error) return null;
  return (
    <tr>
      <td
        colSpan={7}
        className="bg-[hsl(0,70%,96%)] px-3 py-1 text-[12px] text-[hsl(0,70%,35%)]"
      >
        {error}
      </td>
    </tr>
  );
}

export default function EstimationDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const { data: estimation, isLoading } = useEstimation(id ?? "");
  const updateEstimation = useUpdateEstimation(id ?? "");
  const updateStatus = useUpdateEstimationStatus();
  const deleteEstimation = useDeleteEstimation();
  const duplicateEstimation = useDuplicateEstimation();
  const addItem = useAddEstimationItem(id ?? "");
  const updateItem = useUpdateEstimationItem(id ?? "");
  const deleteItem = useDeleteEstimationItem(id ?? "");

  const { data: categoryItems = [] } = useCategoryItems(
    "estimation_cost_category",
  );
  const { data: rateTypeItems = [] } = useCategoryItems(
    "estimation_rate_type",
  );

  const { data: companiesResult } = useCompanyList({
    page: 1,
    limit: 100,
    operationalOnly: true,
  });
  const ctxCompanies = companiesResult?.data ?? [];

  const [ctxCompanyId, setCtxCompanyId] = useState("");
  const [ctxProductId, setCtxProductId] = useState("");
  const [ctxProjectId, setCtxProjectId] = useState("");

  const { data: productsResult } = useProductList({
    page: 1,
    limit: 100,
    companyId: ctxCompanyId || undefined,
  });
  const ctxProducts = productsResult?.data ?? [];

  const { data: projectsResult } = useProjectList({
    page: 1,
    limit: 100,
    companyId: ctxCompanyId || undefined,
    productId: ctxProductId || undefined,
  });
  const ctxProjects = projectsResult?.data ?? [];

  const [editingRow, setEditingRow] = useState<EditingRow | null>(null);
  const [editingHeader, setEditingHeader] = useState(false);
  const [editName, setEditName] = useState("");
  const [editingContingency, setEditingContingency] = useState(false);
  const [contingencyDraft, setContingencyDraft] = useState("");
  const [editingNotes, setEditingNotes] = useState(false);
  const [notesDraft, setNotesDraft] = useState("");
  const [editingContext, setEditingContext] = useState(false);
  const [activityOpen, setActivityOpen] = useState(false);
  const [collapsedCategories, setCollapsedCategories] = useState<
    Record<string, boolean>
  >({});
  const [toast, setToast] = useState<string | null>(null);
  const [rowError, setRowError] = useState<string | null>(null);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<EstimationItem | null>(null);

  function showToast(msg: string) {
    setToast(msg);
    setTimeout(() => setToast(null), 3500);
  }

  const grouped = useMemo(() => {
    const map = new Map<string, EstimationItem[]>();
    if (estimation) {
      for (const item of estimation.items) {
        const arr = map.get(item.category) ?? [];
        arr.push(item);
        map.set(item.category, arr);
      }
    }
    return map;
  }, [estimation]);

  const categoryLabels = useMemo(() => {
    const map = new Map<string, string>();
    for (const c of categoryItems) {
      map.set(c.value, c.label);
    }
    return map;
  }, [categoryItems]);

  const rateTypeLabels = useMemo(() => {
    const map = new Map<string, string>();
    for (const r of rateTypeItems) {
      map.set(r.value, r.label);
    }
    return map;
  }, [rateTypeItems]);

  if (isLoading || !estimation) {
    return (
      <AppLayout
        breadcrumb={[
          { label: "PhazeX Hub", href: "/" },
          { label: "Finance" },
          { label: "Cost Estimations", href: "/estimations" },
        ]}
      >
        <div className="py-12 text-center text-muted-foreground">
          {isLoading ? (
            <>
              <Loader2
                className="mx-auto h-6 w-6 animate-spin"
                strokeWidth={1.5}
              />
              <div className="mt-2 text-sm">Loading…</div>
            </>
          ) : (
            "Estimation not found"
          )}
        </div>
      </AppLayout>
    );
  }

  const currency = estimation.currency;

  async function handleHeaderNameSave() {
    if (!editName.trim()) return;
    await updateEstimation.mutateAsync({ name: editName.trim() });
    setEditingHeader(false);
    showToast("Name updated");
  }

  async function handleContingencySave() {
    const raw = contingencyDraft.trim();
    const pct = raw === "" ? null : Number(raw);
    if (pct !== null && (!Number.isFinite(pct) || pct < 0 || pct > 100)) {
      showToast("Invalid percent");
      return;
    }
    await updateEstimation.mutateAsync({ contingencyPct: pct });
    setEditingContingency(false);
    showToast("Contingency updated");
  }

  async function handleNotesSave() {
    await updateEstimation.mutateAsync({ notes: notesDraft.trim() || null });
    setEditingNotes(false);
    showToast("Notes saved");
  }

  function startEditContext() {
    setCtxCompanyId(estimation!.companyId ?? "");
    setCtxProductId(estimation!.productId ?? "");
    setCtxProjectId(estimation!.projectId ?? "");
    setEditingContext(true);
  }

  async function handleContextSave() {
    await updateEstimation.mutateAsync({
      companyId: ctxCompanyId || null,
      productId: ctxProductId || null,
      projectId: ctxProjectId || null,
    });
    setEditingContext(false);
    showToast("Context updated");
  }

  function handleDelete() {
    setDeleteOpen(true);
  }

  async function confirmDelete() {
    await deleteEstimation.mutateAsync(estimation!.id);
    navigate("/estimations");
  }

  async function handleDuplicate() {
    const res = await duplicateEstimation.mutateAsync(estimation!.id);
    showToast(`Duplicated as ${res.estimation.code}`);
    navigate(`/estimations/${res.estimation.id}`);
  }

  async function handleStatusChange(status: string) {
    await updateStatus.mutateAsync({
      id: estimation!.id,
      status: status as "draft" | "review" | "approved" | "archived",
    });
    showToast(`Status → ${status}`);
  }

  function startEditRow(item: EstimationItem) {
    setRowError(null);
    setEditingRow({
      id: item.id,
      category: item.category,
      description: item.description,
      rateType: item.rateType,
      unitCost: String(item.unitCost),
      quantity: String(item.quantity),
      notes: item.notes ?? "",
    });
  }

  function startNewRow() {
    setRowError(null);
    const firstCategory = categoryItems[0]?.value ?? "human_resources";
    const firstRate = rateTypeItems[0]?.value ?? "monthly";
    setEditingRow(emptyRow(firstCategory, firstRate));
  }

  function cancelEdit() {
    setEditingRow(null);
    setRowError(null);
  }

  async function saveRow() {
    if (!editingRow) return;
    const unitCost = Number(editingRow.unitCost);
    const quantity = Number(editingRow.quantity);
    if (!editingRow.description.trim()) {
      setRowError("Description is required");
      return;
    }
    if (!Number.isFinite(unitCost) || unitCost < 0) {
      setRowError("Unit cost must be a number ≥ 0");
      return;
    }
    if (!Number.isFinite(quantity) || quantity <= 0) {
      setRowError("Quantity must be > 0");
      return;
    }
    setRowError(null);
    try {
      if (editingRow.id) {
        await updateItem.mutateAsync({
          itemId: editingRow.id,
          payload: {
            category: editingRow.category,
            description: editingRow.description.trim(),
            rateType: editingRow.rateType,
            unitCost,
            quantity,
            notes: editingRow.notes.trim() || null,
          },
        });
        showToast("Item updated");
      } else {
        await addItem.mutateAsync({
          category: editingRow.category,
          description: editingRow.description.trim(),
          rateType: editingRow.rateType,
          unitCost,
          quantity,
          notes: editingRow.notes.trim() || null,
        });
        showToast("Item added");
      }
      setEditingRow(null);
    } catch (err) {
      setRowError(err instanceof Error ? err.message : "Failed to save");
    }
  }

  function handleDeleteItem(item: EstimationItem) {
    setItemToDelete(item);
  }

  async function confirmDeleteItem() {
    if (!itemToDelete) return;
    await deleteItem.mutateAsync(itemToDelete.id);
    showToast("Item removed");
  }

  function toggleCategory(cat: string) {
    setCollapsedCategories((prev) => ({ ...prev, [cat]: !prev[cat] }));
  }

  const rowDraftTotal = editingRow
    ? (() => {
        const u = Number(editingRow.unitCost);
        const q = Number(editingRow.quantity);
        if (!Number.isFinite(u) || !Number.isFinite(q)) return 0;
        return Math.round(u * q * 100) / 100;
      })()
    : 0;

  return (
    <AppLayout
      breadcrumb={[
        { label: "PhazeX Hub", href: "/" },
        { label: "Finance" },
        { label: "Cost Estimations", href: "/estimations" },
        { label: estimation.code },
      ]}
    >
      {toast && (
        <div
          className="fixed right-6 top-16 z-[9999] rounded-lg border bg-white px-4 py-3 text-sm font-medium text-green-700 shadow-lg"
          style={{ borderColor: "hsl(142,55%,80%)" }}
        >
          {toast}
        </div>
      )}

      <div className="mb-4 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => navigate("/estimations")}
            className="flex h-8 w-8 items-center justify-center rounded hover:bg-[hsl(210,50%,96%)]"
          >
            <ArrowLeft className="h-4 w-4" strokeWidth={1.5} />
          </button>
          <Calculator
            className="h-[18px] w-[18px] text-foreground"
            strokeWidth={1.5}
          />
          <h1 className="text-xl font-semibold text-foreground">
            Cost Estimation
          </h1>
        </div>
        <div className="flex items-center gap-2">
          <Select
            value={estimation.status}
            onChange={(ev) => handleStatusChange(ev.target.value)}
            className="h-8 w-36 text-xs"
          >
            <option value="draft">Draft</option>
            <option value="review">Review</option>
            <option value="approved">Approved</option>
            <option value="archived">Archived</option>
          </Select>
          <button
            type="button"
            onClick={handleDuplicate}
            className="flex h-8 w-8 items-center justify-center rounded hover:bg-[hsl(210,50%,96%)]"
            title="Duplicate"
          >
            <Copy
              className="h-4 w-4 text-muted-foreground"
              strokeWidth={1.5}
            />
          </button>
          <button
            type="button"
            onClick={() => setActivityOpen(true)}
            className="flex h-8 w-8 items-center justify-center rounded hover:bg-[hsl(210,50%,96%)]"
            title="History"
          >
            <History
              className="h-4 w-4 text-muted-foreground"
              strokeWidth={1.5}
            />
          </button>
          <button
            type="button"
            onClick={handleDelete}
            className="flex h-8 w-8 items-center justify-center rounded hover:bg-[hsl(0,70%,96%)]"
            title="Delete"
          >
            <Trash2
              className="h-4 w-4 text-[hsl(0,70%,45%)]"
              strokeWidth={1.5}
            />
          </button>
        </div>
      </div>

      {/* ROW 1 — Header card */}
      <div
        className="mb-4 rounded-xl border bg-white"
        style={{ borderColor: BORDER, padding: "16px 20px" }}
      >
        {editingHeader ? (
          <div className="flex items-center gap-2">
            <Input
              value={editName}
              onChange={(e) => setEditName(e.target.value)}
              className="text-[20px] font-bold"
              autoFocus
            />
            <button
              type="button"
              onClick={handleHeaderNameSave}
              className="flex h-7 w-7 items-center justify-center rounded text-[hsl(142,72%,28%)] hover:bg-[hsl(142,55%,94%)]"
              title="Save"
            >
              <Check className="h-4 w-4" strokeWidth={1.5} />
            </button>
            <button
              type="button"
              onClick={() => setEditingHeader(false)}
              className="flex h-7 w-7 items-center justify-center rounded text-muted-foreground hover:bg-[hsl(210,50%,96%)]"
              title="Cancel"
            >
              <X className="h-4 w-4" strokeWidth={1.5} />
            </button>
          </div>
        ) : (
          <div
            className="flex cursor-pointer items-center gap-2 text-[20px] font-bold text-foreground"
            onClick={() => {
              setEditName(estimation.name);
              setEditingHeader(true);
            }}
          >
            {estimation.name}
            <Pencil
              className="h-3.5 w-3.5 text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100"
              strokeWidth={1.5}
            />
          </div>
        )}
        <div className="mt-2 flex flex-wrap items-center gap-2 text-[13px] text-muted-foreground">
          <ReferenceIdBadge id={estimation.code} />
          <EstimationStatusBadge status={estimation.status} />
          <span
            className="inline-flex items-center rounded-full px-2.5 py-0.5 text-[11px] font-medium"
            style={{
              background: "hsl(270,55%,92%)",
              color: "hsl(270,60%,35%)",
            }}
          >
            v{estimation.version}
          </span>
          {estimation.projectName && (
            <span className="inline-flex items-center gap-1">
              <FolderKanban className="h-3 w-3" strokeWidth={1.5} />
              <Link
                to={`/projects/${estimation.projectId}`}
                className="hover:text-[hsl(210,100%,35%)]"
              >
                {estimation.projectName}
              </Link>
            </span>
          )}
          {estimation.companyName && (
            <span className="inline-flex items-center gap-1">
              <Building2 className="h-3 w-3" strokeWidth={1.5} />
              <Link
                to={`/companies/${estimation.companyId}`}
                className="hover:text-[hsl(210,100%,35%)]"
              >
                {estimation.companyName}
              </Link>
            </span>
          )}
          <span className="ml-auto text-[11px] text-muted-foreground">
            Created {formatDateTime(estimation.createdAt)}
            {estimation.createdByName ? ` by ${estimation.createdByName}` : ""}
          </span>
        </div>
      </div>

      {/* ROW 2 — Cost table + summary */}
      <div
        className="mb-4 grid gap-4"
        style={{ gridTemplateColumns: "65fr 35fr" }}
      >
        {/* LEFT — Inline editable table */}
        <div
          className="rounded-xl border bg-white"
          style={{ borderColor: BORDER, padding: "16px 20px" }}
        >
          <div className="mb-3 flex items-center justify-between">
            <h3 className="text-[13px] font-semibold text-foreground">
              Cost Items
            </h3>
            <Button size="sm" onClick={startNewRow} disabled={!!editingRow}>
              <Plus className="mr-1 h-3.5 w-3.5" strokeWidth={1.5} />
              Add Item
            </Button>
          </div>

          <div
            className="overflow-hidden rounded-lg border"
            style={{ borderColor: "hsl(210,30%,90%)" }}
          >
            <table
              className="w-full text-[13px]"
              style={{ borderCollapse: "collapse" }}
            >
              <thead className="bg-[hsl(210,30%,97%)] text-left text-xs font-medium uppercase tracking-wide text-muted-foreground">
                <tr>
                  <th className="px-3 py-2">Category</th>
                  <th className="px-3 py-2">Description</th>
                  <th className="px-3 py-2" style={{ width: 100 }}>
                    Rate
                  </th>
                  <th
                    className="px-3 py-2 text-right"
                    style={{ width: 110 }}
                  >
                    Unit Cost
                  </th>
                  <th
                    className="px-3 py-2 text-right"
                    style={{ width: 70 }}
                  >
                    Qty
                  </th>
                  <th
                    className="px-3 py-2 text-right"
                    style={{ width: 140 }}
                  >
                    Total
                  </th>
                  <th
                    className="px-3 py-2 text-right"
                    style={{ width: 80 }}
                  >
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {estimation.items.length === 0 && !editingRow ? (
                  <tr>
                    <td
                      colSpan={7}
                      className="py-10 text-center text-sm text-muted-foreground"
                    >
                      No items yet. Click "Add Item" to start building the
                      estimate.
                    </td>
                  </tr>
                ) : null}
                {Array.from(grouped.entries()).map(([cat, items]) => {
                  const collapsed = collapsedCategories[cat];
                  const categoryTotal = items.reduce(
                    (sum, i) => sum + i.totalCost,
                    0,
                  );
                  return (
                    <Fragment key={`cat-${cat}`}>
                      <tr
                        className="bg-[hsl(210,55%,96%)] cursor-pointer"
                        onClick={() => toggleCategory(cat)}
                      >
                        <td
                          colSpan={5}
                          className="px-3 py-2 text-[12px] font-semibold uppercase tracking-wide text-[hsl(210,100%,28%)]"
                        >
                          <span className="inline-flex items-center gap-1.5">
                            {collapsed ? (
                              <ChevronRight
                                className="h-3 w-3"
                                strokeWidth={2}
                              />
                            ) : (
                              <ChevronDown
                                className="h-3 w-3"
                                strokeWidth={2}
                              />
                            )}
                            {categoryLabels.get(cat) ?? cat}
                          </span>
                        </td>
                        <td
                          className="px-3 py-2 text-right text-[12px] font-semibold text-[hsl(210,100%,28%)]"
                        >
                          {formatCurrency(categoryTotal, currency)}
                        </td>
                        <td className="px-3 py-2"></td>
                      </tr>
                      {!collapsed &&
                        items.map((item) =>
                          editingRow && editingRow.id === item.id ? (
                            <Fragment key={item.id}>
                              <EditableRow
                                row={editingRow}
                                setRow={setEditingRow}
                                categoryItems={categoryItems}
                                rateTypeItems={rateTypeItems}
                                currency={currency}
                                onSave={saveRow}
                                onCancel={cancelEdit}
                                draftTotal={rowDraftTotal}
                                saving={updateItem.isPending}
                              />
                              <RowErrorBanner error={rowError} />
                            </Fragment>
                          ) : (
                            <tr
                              key={item.id}
                              className="border-t transition-colors hover:bg-[hsl(210,50%,98%)]"
                              style={{ borderColor: "hsl(210,30%,92%)" }}
                            >
                              <td className="px-3 py-2 text-muted-foreground">
                                {categoryLabels.get(item.category) ??
                                  item.category}
                              </td>
                              <td className="px-3 py-2 text-foreground">
                                {item.description}
                              </td>
                              <td className="px-3 py-2 text-muted-foreground">
                                {rateTypeLabels.get(item.rateType) ??
                                  item.rateType}
                              </td>
                              <td className="px-3 py-2 text-right text-foreground">
                                {formatCurrency(item.unitCost, currency)}
                              </td>
                              <td className="px-3 py-2 text-right text-foreground">
                                {item.quantity}
                              </td>
                              <td className="px-3 py-2 text-right font-medium text-foreground">
                                {formatCurrency(item.totalCost, currency)}
                              </td>
                              <td className="px-3 py-2 text-right">
                                <div className="flex items-center justify-end gap-1">
                                  <button
                                    type="button"
                                    onClick={() => startEditRow(item)}
                                    className="flex h-7 w-7 items-center justify-center rounded hover:bg-muted text-muted-foreground"
                                    title="Edit"
                                  >
                                    <Pencil
                                      className="h-3.5 w-3.5"
                                      strokeWidth={1.5}
                                    />
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => handleDeleteItem(item)}
                                    className="flex h-7 w-7 items-center justify-center rounded hover:bg-[hsl(0,70%,95%)] text-muted-foreground hover:text-[hsl(0,84%,50%)]"
                                    title="Delete"
                                  >
                                    <Trash2
                                      className="h-3.5 w-3.5"
                                      strokeWidth={1.5}
                                    />
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ),
                        )}
                    </Fragment>
                  );
                })}

                {editingRow && editingRow.id === null && (
                  <>
                    <EditableRow
                      row={editingRow}
                      setRow={setEditingRow}
                      categoryItems={categoryItems}
                      rateTypeItems={rateTypeItems}
                      currency={currency}
                      onSave={saveRow}
                      onCancel={cancelEdit}
                      draftTotal={rowDraftTotal}
                      saving={addItem.isPending}
                      isNew
                    />
                    <RowErrorBanner error={rowError} />
                  </>
                )}
              </tbody>
            </table>
          </div>

          {/* Totals */}
          <div
            className="mt-4 ml-auto w-[320px] rounded-lg border p-4 text-[13px]"
            style={{ borderColor: "hsl(210,30%,90%)" }}
          >
            <div className="flex items-center justify-between py-1">
              <span className="text-muted-foreground">Subtotal</span>
              <span className="text-foreground">
                {formatCurrency(estimation.subtotal, currency)}
              </span>
            </div>
            <div className="flex items-center justify-between py-1">
              <span className="flex items-center gap-1 text-muted-foreground">
                Contingency (
                {editingContingency ? (
                  <>
                    <input
                      type="number"
                      value={contingencyDraft}
                      onChange={(e) => setContingencyDraft(e.target.value)}
                      className="w-[48px] rounded border border-border bg-white px-1 py-0 text-[12px] text-foreground"
                      step="0.5"
                      min="0"
                      max="100"
                      autoFocus
                      onBlur={handleContingencySave}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") handleContingencySave();
                        if (e.key === "Escape") setEditingContingency(false);
                      }}
                    />
                    %
                  </>
                ) : (
                  <button
                    type="button"
                    onClick={() => {
                      setContingencyDraft(
                        estimation.contingencyPct !== null
                          ? String(estimation.contingencyPct)
                          : "0",
                      );
                      setEditingContingency(true);
                    }}
                    className="font-medium text-foreground underline decoration-dotted"
                  >
                    {estimation.contingencyPct ?? 0}%
                  </button>
                )}
                )
              </span>
              <span className="text-foreground">
                {formatCurrency(estimation.contingencyAmount, currency)}
              </span>
            </div>
            <div
              className="mt-2 flex items-center justify-between border-t pt-2 text-[14px] font-bold"
              style={{ borderColor: "hsl(210,30%,88%)" }}
            >
              <span>TOTAL</span>
              <span>{formatCurrency(estimation.grandTotal, currency)}</span>
            </div>
          </div>
        </div>

        {/* RIGHT — Summary & context */}
        <div
          className="rounded-xl border bg-white"
          style={{ borderColor: BORDER, padding: "16px 20px" }}
        >
          <h3 className="mb-3 text-[13px] font-semibold text-foreground">
            Summary by Category
          </h3>
          {estimation.totalByCategory.length === 0 ? (
            <div className="py-6 text-center text-sm text-muted-foreground">
              Add items to see the breakdown.
            </div>
          ) : (
            <div className="space-y-2">
              {estimation.totalByCategory.map((t) => (
                <div key={t.category}>
                  <div className="flex items-center justify-between text-[12px]">
                    <span className="text-foreground">
                      {categoryLabels.get(t.category) ?? t.category}
                    </span>
                    <span className="text-muted-foreground">
                      {formatCurrency(t.total, currency)} ({t.percent}%)
                    </span>
                  </div>
                  <div
                    className="mt-1 h-[6px] w-full rounded-full"
                    style={{ background: "hsl(210,30%,94%)" }}
                  >
                    <div
                      className="h-[6px] rounded-full"
                      style={{
                        width: `${Math.min(100, t.percent)}%`,
                        background: "hsl(210,68%,55%)",
                      }}
                    />
                  </div>
                </div>
              ))}
            </div>
          )}

          <div
            className="mt-6 space-y-2 border-t pt-4 text-[12px]"
            style={{ borderColor: "hsl(210,30%,90%)" }}
          >
            <div className="mb-1 flex items-center justify-between">
              <div className="text-[11px] uppercase text-muted-foreground">
                Context
              </div>
              {!editingContext && (
                <button
                  type="button"
                  onClick={startEditContext}
                  className="flex h-6 w-6 items-center justify-center rounded hover:bg-muted"
                  title="Edit context"
                >
                  <Pencil
                    className="h-3 w-3 text-muted-foreground"
                    strokeWidth={1.5}
                  />
                </button>
              )}
            </div>
            {editingContext ? (
              <div className="space-y-2">
                <div className="relative">
                  <Building2
                    className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground"
                    strokeWidth={1.5}
                  />
                  <select
                    value={ctxCompanyId}
                    onChange={(e) => {
                      setCtxCompanyId(e.target.value);
                      setCtxProductId("");
                      setCtxProjectId("");
                    }}
                    className="flex h-8 w-full rounded-md border border-border bg-background pl-8 pr-2 py-1 text-[12px] shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1"
                  >
                    <option value="">No company</option>
                    {ctxCompanies.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.name} ({c.code})
                      </option>
                    ))}
                  </select>
                </div>
                <div className="relative">
                  <Package
                    className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground"
                    strokeWidth={1.5}
                  />
                  <select
                    value={ctxProductId}
                    onChange={(e) => {
                      setCtxProductId(e.target.value);
                      setCtxProjectId("");
                    }}
                    className="flex h-8 w-full rounded-md border border-border bg-background pl-8 pr-2 py-1 text-[12px] shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1"
                  >
                    <option value="">No product</option>
                    {ctxProducts.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.name} ({p.code})
                      </option>
                    ))}
                  </select>
                </div>
                <div className="relative">
                  <FolderKanban
                    className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground"
                    strokeWidth={1.5}
                  />
                  <select
                    value={ctxProjectId}
                    onChange={(e) => setCtxProjectId(e.target.value)}
                    className="flex h-8 w-full rounded-md border border-border bg-background pl-8 pr-2 py-1 text-[12px] shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1"
                  >
                    <option value="">No project</option>
                    {ctxProjects.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.name} ({p.code})
                      </option>
                    ))}
                  </select>
                </div>
                <div className="flex justify-end gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setEditingContext(false)}
                  >
                    Cancel
                  </Button>
                  <Button size="sm" onClick={handleContextSave}>
                    Save
                  </Button>
                </div>
              </div>
            ) : (
              <>
                {estimation.companyId && (
                  <Link
                    to={`/companies/${estimation.companyId}`}
                    className="flex items-center gap-2 text-foreground hover:text-[hsl(210,100%,35%)]"
                  >
                    <Building2 className="h-3.5 w-3.5" strokeWidth={1.5} />
                    {estimation.companyName}
                  </Link>
                )}
                {estimation.productId && (
                  <Link
                    to={`/products/${estimation.productId}`}
                    className="flex items-center gap-2 text-foreground hover:text-[hsl(210,100%,35%)]"
                  >
                    <Package className="h-3.5 w-3.5" strokeWidth={1.5} />
                    {estimation.productName}
                  </Link>
                )}
                {estimation.projectId && (
                  <Link
                    to={`/projects/${estimation.projectId}`}
                    className="flex items-center gap-2 text-foreground hover:text-[hsl(210,100%,35%)]"
                  >
                    <FolderKanban className="h-3.5 w-3.5" strokeWidth={1.5} />
                    {estimation.projectName}
                  </Link>
                )}
                {!estimation.companyId &&
                  !estimation.productId &&
                  !estimation.projectId && (
                    <div className="text-muted-foreground">
                      No context linked
                    </div>
                  )}
              </>
            )}
          </div>

          <div
            className="mt-4 border-t pt-4"
            style={{ borderColor: "hsl(210,30%,90%)" }}
          >
            <div className="mb-1 flex items-center justify-between">
              <div className="text-[11px] uppercase text-muted-foreground">
                Notes
              </div>
              {!editingNotes && (
                <button
                  type="button"
                  onClick={() => {
                    setNotesDraft(estimation.notes ?? "");
                    setEditingNotes(true);
                  }}
                  className="flex h-6 w-6 items-center justify-center rounded hover:bg-muted"
                  title="Edit notes"
                >
                  <Pencil
                    className="h-3 w-3 text-muted-foreground"
                    strokeWidth={1.5}
                  />
                </button>
              )}
            </div>
            {editingNotes ? (
              <div className="space-y-2">
                <textarea
                  rows={4}
                  value={notesDraft}
                  onChange={(e) => setNotesDraft(e.target.value)}
                  className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
                  onBlur={handleNotesSave}
                />
                <div className="flex justify-end gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setEditingNotes(false)}
                  >
                    Cancel
                  </Button>
                  <Button size="sm" onClick={handleNotesSave}>
                    Save
                  </Button>
                </div>
              </div>
            ) : (
              <div className="whitespace-pre-wrap text-[12px] text-foreground">
                {estimation.notes ?? (
                  <span className="text-muted-foreground">Click to add…</span>
                )}
              </div>
            )}
          </div>

          <div
            className="mt-4 border-t pt-4"
            style={{ borderColor: "hsl(210,30%,90%)" }}
          >
            <div className="mb-2 text-[11px] uppercase text-muted-foreground">
              Linked Proposals
            </div>
            {estimation.linkedProposals.length === 0 ? (
              <div className="text-[12px] text-muted-foreground">
                No proposals reference this estimation yet.
              </div>
            ) : (
              <div className="space-y-1">
                {estimation.linkedProposals.map((p) => (
                  <Link
                    key={p.id}
                    to={`/proposals/${p.id}`}
                    className="flex items-center justify-between rounded px-2 py-1 text-[12px] hover:bg-[hsl(210,50%,96%)]"
                  >
                    <span className="text-foreground">
                      <span className="font-mono text-[11px] text-muted-foreground">
                        {p.code}
                      </span>{" "}
                      — {p.name}
                    </span>
                    <span className="text-muted-foreground">
                      {formatCurrency(p.total, p.currency)}
                    </span>
                  </Link>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      <ActivityHistoryDrawer
        isOpen={activityOpen}
        onClose={() => setActivityOpen(false)}
        entityType="cost_estimation"
        entityId={estimation.id}
        entityLabel={estimation.name}
      />

      <ConfirmDialog
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
        title="Delete estimation?"
        description={`Are you sure you want to delete '${estimation.name}'? It will be removed from your Estimations list.`}
        onConfirm={confirmDelete}
      />

      <ConfirmDialog
        open={itemToDelete !== null}
        onOpenChange={(open) => {
          if (!open) setItemToDelete(null);
        }}
        title="Delete line item?"
        description="Are you sure you want to delete this line item? It will be removed from the estimation."
        onConfirm={confirmDeleteItem}
      />
    </AppLayout>
  );
}

type EditableRowProps = {
  row: EditingRow;
  setRow: (row: EditingRow) => void;
  categoryItems: { id: string; value: string; label: string }[];
  rateTypeItems: { id: string; value: string; label: string }[];
  currency: string;
  onSave: () => void;
  onCancel: () => void;
  draftTotal: number;
  saving: boolean;
  isNew?: boolean;
};

function EditableRow({
  row,
  setRow,
  categoryItems,
  rateTypeItems,
  currency,
  onSave,
  onCancel,
  draftTotal,
  saving,
  isNew = false,
}: EditableRowProps) {
  return (
    <tr
      className="border-t"
      style={{
        background: "hsl(210,55%,96%)",
        borderColor: "hsl(210,55%,85%)",
      }}
    >
      <td className="px-3 py-2">
        <Select
          value={row.category}
          onChange={(e) => setRow({ ...row, category: e.target.value })}
          className="h-8 text-[12px]"
        >
          {categoryItems.length === 0 ? (
            <option value={row.category}>{row.category}</option>
          ) : (
            categoryItems.map((c) => (
              <option key={c.id} value={c.value}>
                {c.label}
              </option>
            ))
          )}
        </Select>
      </td>
      <td className="px-3 py-2">
        <Input
          value={row.description}
          onChange={(e) => setRow({ ...row, description: e.target.value })}
          placeholder="e.g. Senior Developer"
          className="h-8 text-[12px]"
          autoFocus={isNew}
          onKeyDown={(e) => {
            if (e.key === "Enter") onSave();
            if (e.key === "Escape") onCancel();
          }}
        />
      </td>
      <td className="px-3 py-2">
        <Select
          value={row.rateType}
          onChange={(e) => setRow({ ...row, rateType: e.target.value })}
          className="h-8 text-[12px]"
        >
          {rateTypeItems.length === 0 ? (
            <option value={row.rateType}>{row.rateType}</option>
          ) : (
            rateTypeItems.map((r) => (
              <option key={r.id} value={r.value}>
                {r.label}
              </option>
            ))
          )}
        </Select>
      </td>
      <td className="px-3 py-2">
        <Input
          type="number"
          value={row.unitCost}
          step="0.01"
          min="0"
          onChange={(e) => setRow({ ...row, unitCost: e.target.value })}
          className="h-8 text-right text-[12px]"
          onKeyDown={(e) => {
            if (e.key === "Enter") onSave();
            if (e.key === "Escape") onCancel();
          }}
        />
      </td>
      <td className="px-3 py-2">
        <Input
          type="number"
          value={row.quantity}
          step="0.5"
          min="0"
          onChange={(e) => setRow({ ...row, quantity: e.target.value })}
          className="h-8 text-right text-[12px]"
          onKeyDown={(e) => {
            if (e.key === "Enter") onSave();
            if (e.key === "Escape") onCancel();
          }}
        />
      </td>
      <td className="px-3 py-2 text-right font-medium text-foreground">
        {formatCurrency(draftTotal, currency)}
      </td>
      <td className="px-3 py-2 text-right">
        <div className="flex items-center justify-end gap-1">
          <button
            type="button"
            onClick={onSave}
            disabled={saving}
            className="flex h-7 w-7 items-center justify-center rounded text-[hsl(142,72%,28%)] hover:bg-[hsl(142,55%,94%)] disabled:opacity-50"
            title="Save (Enter)"
          >
            {saving ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" strokeWidth={1.5} />
            ) : (
              <Check className="h-3.5 w-3.5" strokeWidth={1.5} />
            )}
          </button>
          <button
            type="button"
            onClick={onCancel}
            className="flex h-7 w-7 items-center justify-center rounded text-muted-foreground hover:bg-muted"
            title="Cancel (Esc)"
          >
            <X className="h-3.5 w-3.5" strokeWidth={1.5} />
          </button>
        </div>
      </td>
    </tr>
  );
}
